"use client";

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPost, apiPut, apiDelete, apiPatch, uploadFile } from "@/lib/api-client";
import type { Product } from "@/lib/api-client";
import { useAppStore, currentView } from "@/lib/store";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/inline-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AvailabilityModal } from "@/components/booking/availability-modal";
import { ImageManagerModal } from "@/components/booking/image-manager-modal";
import { SeasonsModal } from "@/components/booking/seasons-modal";import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/inline-dialog";
import {
  Bed,
  Bath,
  Users,
  MapPin,
  CalendarDays,
  CalendarRange,
  CheckCircle2,
  Tag,
  ArrowLeft,
  Lock,
  Users2,
  Pencil,
  Power,
  Trash2,
  ImageIcon,
  Wrench,
  Camera,
  Loader2,
  Plus,
  UserPlus,
} from "lucide-react";
import {
  formatDate,
  formatDateShort,
  formatPrice,
  nightsBetween,
  amenityList,
  propertyTypeLabel,
  bookingTypeLabel,
  bookingTypeBadgeClass,
} from "@/components/booking/format";
import { toast } from "sonner";
import { useMemo, useState, useRef } from "react";import { useI18n } from "@/lib/i18n/provider";

export function ProductDetailView() {
  const id = useAppStore((s) => s.selectedProductId);
  const back = useAppStore((s) => s.back);
  const user = useAppStore((s) => s.user);
  const openAuth = useAppStore((s) => s.openAuth);
  const openEditProduct = useAppStore((s) => s.openEditProduct);
  const qc = useQueryClient();
  const { t, locale } = useI18n();

  const [checkIn, setCheckIn] = useState("");
  const [checkOut, setCheckOut] = useState("");
  const [partySize, setPartySize] = useState(1);
  const [busy, setBusy] = useState(false);
  const [activeImage, setActiveImage] = useState(0);

  const { data, isLoading } = useQuery({
    queryKey: ["product", id],
    queryFn: () =>
      apiGet<{
        product: Product;
        viewer: { canEdit: boolean; canValidate: boolean; canManageStaff: boolean };
      }>(`/api/products/${id}`),
    enabled: !!id,
  });

  const product = data?.product;
  const viewer = data?.viewer;
  const images = product?.images ?? [];
  const nights =
    checkIn && checkOut
      ? nightsBetween(new Date(checkIn).toISOString(), new Date(checkOut).toISOString())
      : 0;

  const bookedRanges =
    product?.reservations?.filter((r) => r.status === "APPROVED") ?? [];

  // The stay's booking type is a per-request choice (private exclusive vs
  // prorata shared) — the property itself allows both.
  const [bookingType, setBookingType] = useState<"PRIVATE" | "SHARED">("PRIVATE");
  const isShared = bookingType === "SHARED";

  // Per-type availability on the chosen dates:
  //   PRIVATE request  → blocked by any approved overlap (exclusive).
  //   SHARED request   → blocked by an approved PRIVATE overlap; shares capacity
  //                      with other approved SHARED stays.
  const overlapsChosen = (list: typeof bookedRanges) => {
    if (!checkIn || !checkOut) return false;
    const s = new Date(checkIn).getTime();
    const e = new Date(checkOut).getTime();
    return list.some(
      (r) => new Date(r.endDate).getTime() > s && new Date(r.startDate).getTime() < e
    );
  };
  const approvedPrivateOverlap = overlapsChosen(
    bookedRanges.filter((r) => r.bookingType === "PRIVATE")
  );
  const approvedAnyOverlap = overlapsChosen(bookedRanges);

  // ---- Shared booking: projected price + co-occupants ----
  const approvedShared = bookedRanges.filter((r) => r.bookingType === "SHARED");
  const coOccupants = useMemo(() => {
    if (!isShared || !checkIn || !checkOut) return [];
    const start = new Date(checkIn).getTime();
    const end = new Date(checkOut).getTime();
    return approvedShared.filter(
      (r) => new Date(r.endDate).getTime() > start && new Date(r.startDate).getTime() < end
    );
  }, [isShared, checkIn, checkOut, approvedShared]);

  // Projected total for shared = sum over days of (partySize / (coOccupantsThatDay + partySize)) * rate
  const projectedSharedTotal = useMemo(() => {
    if (!isShared || !product || nights <= 0) return 0;
    const rate = product.pricePerNight;
    let total = 0;
    const start = new Date(checkIn);
    start.setUTCHours(0, 0, 0, 0);
    for (let i = 0; i < nights; i++) {
      const day = new Date(start);
      day.setUTCDate(day.getUTCDate() + i);
      const dayMs = day.getTime();
      const occ =
        coOccupants
          .filter((r) => new Date(r.startDate).getTime() <= dayMs && dayMs < new Date(r.endDate).getTime())
          .reduce((s, r) => s + r.partySize, 0) + Math.max(1, partySize);
      total += (Math.max(1, partySize) / occ) * rate;
    }
    return Math.round(total * 100) / 100;
  }, [isShared, product, nights, checkIn, coOccupants, partySize]);

  const totalPrice = isShared ? projectedSharedTotal : product ? nights * product.pricePerNight : 0;

  const reserve = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) {
      openAuth("login");
      return;
    }
    if (!checkIn || !checkOut) {
      toast.error(t("toast.pleaseChooseDates"));
      return;
    }
    setBusy(true);
    try {
      await apiPost("/api/reservations", {
        productId: id,
        bookingType,
        startDate: checkIn,
        endDate: checkOut,
        partySize: Math.max(1, partySize),
      });
      toast.success(t("toast.reservationRequested"));
      setCheckIn("");
      setCheckOut("");
      setPartySize(1);
      qc.invalidateQueries({ queryKey: ["product", id] });
      qc.invalidateQueries({ queryKey: ["reservations"] });
    } catch (err: any) {
      toast.error(err.message || t("toast.couldNotCreate"));
    } finally {
      setBusy(false);
    }
  };

  // ---- On-page management (admin / owning owner) ----
  const canManage = !!viewer && viewer.canEdit;
  const canManageStaff = !!viewer && viewer.canManageStaff;
  const [manageFor, setManageFor] = useState<
    null | "availability" | "images" | "seasons"
  >(null);
  const [deleteOpen, setDeleteOpen] = useState(false);

  // House staff (moderators assigned to this specific house).
  const [staffOpen, setStaffOpen] = useState(false);
  const { data: staffData } = useQuery({
    queryKey: ["product-staff", id],
    queryFn: () =>
      apiGet<{
        staff: { id: string; name: string; username: string }[];
        candidates: { id: string; name: string; username: string }[];
      }>(`/api/products/${id}/staff`),
    enabled: staffOpen && canManageStaff && !!product,
  });
  const houseStaff = staffData?.staff ?? [];
  const staffCandidates = staffData?.candidates ?? [];

  const addStaff = async (userId: string) => {
    try {
      await apiPost(`/api/products/${id}/staff`, { userId });
      toast.success(t("pd.staffAdded"));
      qc.invalidateQueries({ queryKey: ["product-staff", id] });
    } catch (err: any) {
      toast.error(err.message || "Could not add");
    }
  };

  const removeStaff = async (userId: string) => {
    try {
      await apiDelete(`/api/products/${id}/staff?userId=${userId}`);
      toast.success(t("pd.staffRemoved"));
      qc.invalidateQueries({ queryKey: ["product-staff", id] });
    } catch (err: any) {
      toast.error(err.message || "Could not remove");
    }
  };

  // Click-to-change photos: a hidden file input is triggered by clicking the
  // main image, a thumbnail's camera badge, or the add-photo tile.
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [uploadTarget, setUploadTarget] = useState<number | null>(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  const toggleActive = async () => {
    if (!product) return;
    try {
      await apiPut(`/api/products/${product.id}`, { active: !product.active });
      toast.success(
        product.active ? t("toast.propertyHidden") : t("toast.propertyPublished")
      );
      qc.invalidateQueries({ queryKey: ["product", id] });
      qc.invalidateQueries({ queryKey: ["products"] });
    } catch (err: any) {
      toast.error(err.message || t("toast.couldNotUpdate"));
    }
  };

  const confirmDelete = async () => {
    if (!product) return;
    try {
      await apiDelete(`/api/products/${product.id}`);
      toast.success(t("toast.propertyDeleted"));
      qc.invalidateQueries({ queryKey: ["products"] });
      back();
    } catch (err: any) {
      toast.error(err.message || t("toast.couldNotDelete"));
    }
  };

  // Replace the photo at `index` (or append when index === images.length):
  // upload the file, place the new image at the old position, drop the old one.
  const onPhotoChosen = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !product || uploadTarget === null) return;
    setUploadingPhoto(true);
    try {
      const { url } = await uploadFile("/api/upload", file);
      if (uploadTarget >= images.length) {
        // Append a new photo at the end.
        await apiPost(`/api/products/${product.id}/images`, { url, caption: null });
      } else {
        const old = images[uploadTarget];
        const res = await apiPost<{ image: { id: string } }>(
          `/api/products/${product.id}/images`,
          { url, caption: old.caption }
        );
        // Move the new image into the old slot, then remove the old file.
        await apiPatch(
          `/api/products/${product.id}/images/${res.image.id}`,
          { order: old.order }
        );
        await apiDelete(`/api/products/${product.id}/images/${old.id}`);
        setActiveImage(Math.min(uploadTarget, images.length - 1));
      }
      toast.success(t("pd.photoUpdated"));
      qc.invalidateQueries({ queryKey: ["product", id] });
      qc.invalidateQueries({ queryKey: ["products"] });
    } catch (err: any) {
      toast.error(err.message || t("toast.uploadFailed"));
    } finally {
      setUploadingPhoto(false);
      setUploadTarget(null);
    }
  };

  const pickPhoto = (index: number) => {
    if (uploadingPhoto) return;
    setUploadTarget(index);
    fileInputRef.current?.click();
  };

  if (isLoading) {
    return (
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6">
        <Skeleton className="mb-6 h-6 w-32" />
        <div className="grid gap-8 lg:grid-cols-3">
          <Skeleton className="aspect-[16/10] lg:col-span-2 w-full rounded-2xl" />
          <Skeleton className="h-64 w-full rounded-2xl" />
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="mx-auto max-w-7xl px-4 py-16 text-center sm:px-6">
        <p className="text-lg font-medium">{t("common.loading")}</p>
        <Button onClick={back} className="mt-4">
          {t("detail.backToAll")}
        </Button>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6">
      <Button variant="ghost" size="sm" onClick={back} className="mb-4 gap-2">
        <ArrowLeft className="h-4 w-4" /> {t("detail.backToAll")}
      </Button>

      {/* On-the-go management bar (admin / owning owner only) */}
      {canManage && (
        <div className="mb-6 flex flex-wrap items-center gap-2 rounded-xl border bg-card p-3">
          <span className="me-auto flex items-center gap-2 text-sm font-medium">
            <Wrench className="h-4 w-4 text-amber-600" /> {t("pd.manage")}
            {!product.active && (
              <Badge variant="outline" className="border-zinc-300 text-zinc-500">
                {t("host.hidden")}
              </Badge>
            )}
          </span>
          <Button
            size="sm"
            variant="outline"
            className="gap-1.5"
            onClick={() => openEditProduct(product.id)}
          >
            <Pencil className="h-3.5 w-3.5" /> {t("common.edit")}
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="gap-1.5"
            onClick={() => setStaffOpen(true)}
          >
            <UserPlus className="h-3.5 w-3.5" /> {t("pd.staff")}
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="gap-1.5"
            onClick={() => setManageFor("availability")}
          >
            <CalendarRange className="h-3.5 w-3.5" /> {t("host.availability")}
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="gap-1.5"
            onClick={() => setManageFor("images")}
          >
            <ImageIcon className="h-3.5 w-3.5" /> {t("host.photos", { count: images.length })}
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="gap-1.5"
            onClick={() => setManageFor("seasons")}
          >
            <CalendarRange className="h-3.5 w-3.5" /> {t("season.ranges")}
          </Button>
          <Button size="sm" variant="outline" className="gap-1.5" onClick={toggleActive}>
            <Power className="h-3.5 w-3.5" /> {product.active ? t("common.hide") : t("common.publish")}
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="gap-1.5 text-destructive hover:text-destructive"
            onClick={() => setDeleteOpen(true)}
          >
            <Trash2 className="h-3.5 w-3.5" /> {t("common.delete")}
          </Button>
        </div>
      )}

      <div className="grid gap-8 lg:grid-cols-3">
        {/* Left: gallery + info */}
        <div className="lg:col-span-2">
          <div className="group relative overflow-hidden rounded-2xl bg-muted">
            <div className="relative aspect-[16/10] w-full">
              {images[activeImage] ? (
                <img
                  src={images[activeImage].url}
                  alt={images[activeImage].caption || product.title}
                  className="h-full w-full object-cover"
                />
              ) : (
                <div className="grid h-full w-full place-items-center text-muted-foreground">
                  {t("common.noImage")}
                </div>
              )}
              <Badge className="absolute left-4 top-4 bg-background/90 text-foreground">
                {propertyTypeLabel(product.propertyType, locale)}
              </Badge>
              {canManage && (
                <button
                  type="button"
                  onClick={() => pickPhoto(activeImage)}
                  disabled={uploadingPhoto}
                  className="absolute inset-x-0 bottom-0 flex items-center justify-center gap-2 bg-gradient-to-t from-black/70 to-transparent py-4 text-sm font-medium text-white opacity-0 transition-opacity hover:opacity-100 focus-visible:opacity-100 disabled:opacity-60 group-hover:opacity-100"
                >
                  <Camera className="h-4 w-4" /> {t("pd.changePhoto")}
                </button>
              )}
              {uploadingPhoto && uploadTarget === activeImage && (
                <div className="absolute inset-0 grid place-items-center bg-background/60">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              )}
            </div>
          </div>

          {(images.length > 1 || canManage) && (
            <div className="mt-3 flex gap-2 overflow-x-auto pb-1">
              {images.map((im, i) => (
                <div key={im.id} className="relative shrink-0">
                  <button
                    type="button"
                    onClick={() => setActiveImage(i)}
                    className={`relative block aspect-[4/3] w-28 overflow-hidden rounded-lg border-2 transition ${
                      i === activeImage ? "border-primary" : "border-transparent opacity-80 hover:opacity-100"
                    }`}
                  >
                    <img src={im.url} alt={im.caption || ""} className="h-full w-full object-cover" />
                  </button>
                  {canManage && (
                    <button
                      type="button"
                      title={t("pd.changePhoto")}
                      aria-label={t("pd.changePhoto")}
                      disabled={uploadingPhoto}
                      onClick={() => pickPhoto(i)}
                      className="absolute end-1 top-1 grid h-6 w-6 place-items-center rounded-md bg-background/85 text-foreground transition-colors hover:bg-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60"
                    >
                      <Camera className="h-3.5 w-3.5" />
                    </button>
                  )}
                  {uploadingPhoto && uploadTarget === i && (
                    <div className="absolute inset-0 grid place-items-center rounded-lg bg-background/60">
                      <Loader2 className="h-4 w-4 animate-spin text-primary" />
                    </div>
                  )}
                </div>
              ))}
              {canManage && (
                <button
                  type="button"
                  onClick={() => pickPhoto(images.length)}
                  disabled={uploadingPhoto}
                  title={t("pd.addPhoto")}
                  className="grid aspect-[4/3] w-28 shrink-0 place-items-center rounded-lg border-2 border-dashed text-muted-foreground transition-colors hover:border-primary hover:text-primary disabled:opacity-60"
                >
                  <Plus className="h-5 w-5" />
                </button>
              )}
            </div>
          )}

          <div className="mt-6 space-y-4">
            <div>
              <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
                {product.title}
              </h1>
              <div className="mt-1 flex items-center gap-1 text-muted-foreground">
                <MapPin className="h-4 w-4" />
                {product.location}
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-6 border-y py-4">
              <Stat icon={Bed} label={`${product.bedrooms}`} />
              <Stat icon={Bath} label={`${product.bathrooms}`} />
              <Stat icon={Users} label={`${product.maxGuests} ${t("guests")}`} />
              <Stat icon={Tag} label={`${formatPrice(product.pricePerNight)} ${t("card.perNight")}`} />
            </div>

            <div>
              <h2 className="mb-2 text-lg font-semibold">{t("detail.about")}</h2>
              <p className="whitespace-pre-line leading-relaxed text-muted-foreground">
                {product.description}
              </p>
            </div>

            {amenityList(product.amenities).length > 0 && (
              <div>
                <h2 className="mb-2 text-lg font-semibold">{t("detail.amenities")}</h2>
                <div className="flex flex-wrap gap-2">
                  {amenityList(product.amenities).map((a) => (
                    <Badge key={a} variant="secondary" className="gap-1">
                      <CheckCircle2 className="h-3.5 w-3.5" /> {a}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            <div>
              <h2 className="mb-2 text-lg font-semibold">{t("detail.availability")}</h2>
              {product.availabilities && product.availabilities.length > 0 ? (
                <ul className="grid gap-2 sm:grid-cols-2">
                  {product.availabilities.map((a) => (
                    <li
                      key={a.id}
                      className="flex items-center gap-2 rounded-lg border bg-card px-3 py-2 text-sm"
                    >
                      <CalendarDays className="h-4 w-4 text-emerald-600" />
                      {formatDate(a.startDate, locale)} — {formatDate(a.endDate, locale)}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-muted-foreground">{t("detail.noAvailability")}</p>
              )}
            </div>

            {bookedRanges.length > 0 && (
              <div>
                <h2 className="mb-2 text-lg font-semibold">{t("detail.alreadyBooked")}</h2>
                <ul className="flex flex-wrap gap-2">
                  {bookedRanges.map((r) => (
                    <li key={r.id}>
                      {r.bookingType === "SHARED" ? (
                        <Badge
                          variant="outline"
                          className="gap-1 border-violet-200 bg-violet-50 text-violet-800"
                        >
                          <Users2 className="h-3 w-3" /> {formatDateShort(r.startDate, locale)} —{" "}
                          {formatDateShort(r.endDate, locale)} · {r.partySize} {t("guests")}
                        </Badge>
                      ) : (
                        <Badge
                          variant="outline"
                          className="gap-1 border-rose-200 bg-rose-50 text-rose-700"
                        >
                          <Lock className="h-3 w-3" /> {formatDateShort(r.startDate, locale)} —{" "}
                          {formatDateShort(r.endDate, locale)}
                        </Badge>
                      )}
                    </li>
                  ))}
                </ul>
                <p className="mt-2 text-xs text-muted-foreground">{t("detail.bookedLegend")}</p>
              </div>
            )}
          </div>
        </div>

        {/* Right: reservation panel */}
        <div>
          <Card className="sticky top-20">
            <CardHeader>
              <CardTitle className="flex items-baseline justify-between">
                <span>{formatPrice(product.pricePerNight)}</span>
                <span className="text-sm font-normal text-muted-foreground">
                  {t("detail.perNight")}
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {!user ? (
                <div className="grid gap-3">
                  <Button size="lg" className="w-full" onClick={() => openAuth("login")}>
                    {t("detail.signInToReserve")}
                  </Button>
                  <p className="text-center text-xs text-muted-foreground">
                    {t("detail.requestHelpText")}
                  </p>
                </div>
              ) : (
              <form onSubmit={reserve} className="grid gap-4">
                {/* Booking type chooser: per-stay decision */}
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setBookingType("PRIVATE")}
                    className={`rounded-lg border p-3 text-start text-sm transition ${
                      !isShared
                        ? "border-rose-400 bg-rose-50 text-rose-900 ring-1 ring-rose-300"
                        : "border-border bg-card text-muted-foreground hover:border-sky-200"
                    }`}
                  >
                    <div className="flex items-center gap-1.5 font-medium">
                      <Lock className="h-4 w-4" /> {t("booking.privateTitle")}
                    </div>
                    <div className="mt-0.5 text-[11px] leading-tight">
                      {t("booking.PRIVATE.short")}
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setBookingType("SHARED")}
                    className={`rounded-lg border p-3 text-start text-sm transition ${
                      isShared
                        ? "border-sky-400 bg-sky-50 text-sky-900 ring-1 ring-sky-300"
                        : "border-border bg-card text-muted-foreground hover:border-sky-200"
                    }`}
                  >
                    <div className="flex items-center gap-1.5 font-medium">
                      <Users2 className="h-4 w-4" /> {t("booking.sharedTitle")}
                    </div>
                    <div className="mt-0.5 text-[11px] leading-tight">
                      {t("booking.SHARED.short")}
                    </div>
                  </button>
                </div>
                <p className="-mt-1 text-xs leading-relaxed text-muted-foreground">
                  {isShared ? t("booking.SHARED.desc") : t("booking.PRIVATE.desc")}
                </p>
                {approvedPrivateOverlap && nights > 0 && (
                  <p className="rounded-md border border-rose-200 bg-rose-50 p-2 text-xs text-rose-700 dark:border-rose-900 dark:bg-rose-950 dark:text-rose-300">
                    {t("detail.privateBlockHint")}
                  </p>
                )}

                <div className="grid grid-cols-2 gap-3">
                  <div className="grid gap-1.5">
                    <Label htmlFor="checkIn">{t("detail.checkIn")}</Label>
                    <Input
                      id="checkIn"
                      type="date"
                      value={checkIn}
                      onChange={(e) => setCheckIn(e.target.value)}
                      required
                    />
                  </div>
                  <div className="grid gap-1.5">
                    <Label htmlFor="checkOut">{t("detail.checkOut")}</Label>
                    <Input
                      id="checkOut"
                      type="date"
                      value={checkOut}
                      onChange={(e) => setCheckOut(e.target.value)}
                      required
                    />
                  </div>
                </div>

                {/* Party size (always shown; meaningful for both, drives shared split) */}
                <div className="grid gap-1.5">
                  <Label htmlFor="partySize">
                    {isShared ? t("booking.field.partySize") : t("booking.field.partySize")}
                  </Label>
                  <Input
                    id="partySize"
                    type="number"
                    min={1}
                    max={product?.maxGuests ?? 100}
                    value={partySize}
                    onChange={(e) =>
                      setPartySize(Math.max(1, Math.min(Number(e.target.value) || 1, product?.maxGuests ?? 100)))
                    }
                    required
                  />
                  <p className="text-xs text-muted-foreground">{t("booking.field.partySizeHint")}</p>
                </div>

                {nights > 0 && (
                  <div className="space-y-1 rounded-lg border bg-muted/40 p-3 text-sm">
                    {isShared ? (
                      <>
                        <Row
                          label={t("booking.yourShare")}
                          value={formatPrice(totalPrice)}
                        />
                        <p className="text-xs text-muted-foreground">
                          {t("booking.yourShareHint")}
                        </p>
                      </>
                    ) : (
                      <Row
                        label={t("detail.priceRow", {
                          price: formatPrice(product.pricePerNight),
                          nights,
                          plural: nights !== 1 ? "s" : "",
                        })}
                        value={formatPrice(totalPrice)}
                      />
                    )}
                    <div className="flex justify-between border-t pt-1 font-semibold">
                      <span>{t("common.total")}</span>
                      <span>{formatPrice(totalPrice)}</span>
                    </div>
                  </div>
                )}

                {isShared && coOccupants.length > 0 && nights > 0 && (
                  <div className="rounded-lg border border-violet-200 bg-violet-50/50 p-3 text-sm">
                    <div className="mb-1 flex items-center gap-2 font-medium text-violet-900">
                      <Users2 className="h-4 w-4" />
                      {t("booking.coOccupants")}
                    </div>
                    <ul className="space-y-1 text-muted-foreground">
                      {coOccupants.map((c) => (
                        <li key={c.id} className="flex items-center justify-between gap-2">
                          <span>
                            {formatDateShort(c.startDate, locale)} — {formatDateShort(c.endDate, locale)}
                          </span>
                          <Badge variant="outline" className="border-violet-200 text-violet-800">
                            {c.partySize} {c.partySize === 1 ? t("guests") : t("guests")}
                          </Badge>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {isShared && coOccupants.length === 0 && nights > 0 && (
                  <p className="text-xs text-muted-foreground">
                    {t("booking.noCoOccupants")}
                  </p>
                )}

                <Button type="submit" disabled={busy} size="lg" className="w-full">
                  {busy ? t("detail.requesting") : t("detail.requestReservation")}
                </Button>
                <p className="text-center text-xs text-muted-foreground">
                  {t("detail.requestHelpText")}
                </p>
              </form>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Hidden file input for click-to-change photos */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={onPhotoChosen}
      />

      {/* Management modals (admin / owning owner) */}
      {canManage && (
        <>
          <AvailabilityModal
            productId={id}
            open={manageFor === "availability"}
            onOpenChange={(o) => !o && setManageFor(null)}
          />
          <ImageManagerModal
            productId={id}
            open={manageFor === "images"}
            onOpenChange={(o) => !o && setManageFor(null)}
          />
          <SeasonsModal
            productId={id}
            highSeasonRate={product.highSeasonRate ?? 0}
            open={manageFor === "seasons"}
            onOpenChange={(o) => !o && setManageFor(null)}
          />
          <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>{t("host.deleteTitle")}</AlertDialogTitle>
                <AlertDialogDescription>{t("host.deleteBody")}</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
                <AlertDialogAction
                  onClick={confirmDelete}
                  className="bg-destructive text-white hover:bg-destructive/90"
                >
                  {t("common.delete")}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          <Dialog open={staffOpen} onOpenChange={setStaffOpen}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>{t("pd.staff")}</DialogTitle>
                <DialogDescription>{t("pd.staffHint")}</DialogDescription>
              </DialogHeader>
              <div className="grid gap-2">
                {houseStaff.length === 0 ? (
                  <p className="rounded-lg border border-dashed p-3 text-center text-xs text-muted-foreground">
                    {t("pd.staffEmpty")}
                  </p>
                ) : (
                  houseStaff.map((m) => (
                    <div
                      key={m.id}
                      className="flex items-center justify-between gap-2 rounded-lg border px-3 py-2 text-sm"
                    >
                      <div className="min-w-0">
                        <div className="truncate font-medium">{m.name}</div>
                        <div className="text-xs text-muted-foreground">@{m.username}</div>
                      </div>
                      <Button
                        size="icon"
                        variant="ghost"
                        title={t("pd.staffRemove")}
                        className="text-muted-foreground hover:text-destructive"
                        onClick={() => removeStaff(m.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))
                )}
              </div>
              {staffCandidates.length > 0 && (
                <Select
                  value=""
                  onValueChange={(v) => {
                    if (v) addStaff(v);
                  }}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder={t("pd.addStaff")} />
                  </SelectTrigger>
                  <SelectContent>
                    {staffCandidates.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name} (@{c.username})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </DialogContent>
          </Dialog>
        </>
      )}
    </div>
  );
}

function Stat({ icon: Icon, label }: { icon: React.ComponentType<{ className?: string }>; label: string }) {
  return (
    <div className="flex items-center gap-2 text-sm">
      <Icon className="h-4 w-4 text-muted-foreground" />
      {label}
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between">
      <span className="text-muted-foreground">{label}</span>
      <span>{value}</span>
    </div>
  );
}
